import { useState } from 'react'
import cn from 'classnames'
import { Form, Field } from 'react-final-form'
import { CertificateProgramEngagement } from '@mc/api'
import { ArrowCta } from '@mc/certificates-design-system'
import { CheckboxField, InputField } from '@mc/design-system'
import { useTranslation, Trans } from '@mc/i18n'
import { paths } from '@mc/paths'
import { useCertificateSlug } from 'pages/certificates/common'
import { useFireEvent } from 'pages/certificates/tracking'
import { validateEmailWithGdpr } from 'pages/certificates/validations'
import classes from './EmailCapture.module.scss'

type Props = {
  emailCapture: CertificateProgramEngagement['emailCapture']
}

export const EmailCapture = ({ emailCapture }: Props) => {
  const { certSlug } = useCertificateSlug()
  const [submitted, setSubmitted] = useState<boolean>(false)
  const fireEvent = useFireEvent()

  const { t } = useTranslation('@mc/certificates')

  const onSubmit = ({
    email,
    gdprOptIn,
  }: {
    email: string
    gdprOptIn: boolean
  }) => {
    setSubmitted(true)

    fireEvent('certificateEmailCaptured', {
      action: t('emailCapture.signUp'),
      email,
      campaign: certSlug,
      gdpr_opt_in: gdprOptIn,
      location: 'coming soon',
    })
  }

  return submitted ? (
    <p aria-live='polite'>{t('emailCapture.thankYou')}</p>
  ) : (
    <Form
      validate={validateEmailWithGdpr}
      onSubmit={onSubmit}
      initialValues={{
        gdprOptIn: false,
      }}
      render={({ handleSubmit, valid }) => (
        <form
          onSubmit={handleSubmit}
          data-testid='certificate-email-capture-form'
        >
          <div className=''>
            <div className='d-flex flex-column flex-gap-2'>
              <label htmlFor='email' className='c-text-color--dark c-body-bold'>
                {emailCapture?.headline}
              </label>
              <div className='d-flex flex-column flex-sm-row flex-gap-2'>
                <Field
                  render={({ input, meta, ...restProps }) => {
                    // Filter out error-related properties from meta to suppress default error display
                    const metaWithoutErrors = {
                      ...meta,
                      error: undefined,
                      submitError: undefined,
                      invalid: false,
                    }

                    return (
                      <InputField
                        {...restProps}
                        input={input}
                        meta={metaWithoutErrors}
                        inputClassName={cn(
                          'c-text-color--dark mc-corners--sm',
                          { [classes.emailInput]: !input.value },
                        )}
                      />
                    )
                  }}
                  name='email'
                  id='email'
                  type='email'
                  placeholder={t('emailPlaceholder')}
                  autoComplete='email'
                  required
                  className='mc-w-100 mc-mt-2'
                />
                <ArrowCta
                  className='d-flex mc-h-100 mc-mt-auto'
                  type='submit'
                  disabled={!valid}
                >
                  {t('emailCapture.signUp')}
                </ArrowCta>
              </div>
            </div>
            <Field
              render={({ input, meta, ...restProps }) => {
                // Filter out error-related properties from meta to suppress default error display
                const metaWithoutErrors = {
                  ...meta,
                  error: undefined,
                  submitError: undefined,
                  invalid: false,
                }
                return (
                  <CheckboxField
                    {...restProps}
                    input={input}
                    meta={metaWithoutErrors}
                    className={cn(
                      classes.checkbox,
                      { [classes.checked]: input.value },
                      'mc-text-x-small c-text-color--tint mc-mt-2',
                    )}
                  />
                )
              }}
              name='gdprOptIn'
              label={t('marketingPages.certificate.updatesSignup.gdpr')}
              className='mc-text-x-small c-text-color--tint mc-mt-2'
              required
            />
            <p className='c-text-color--tint mc-text-x-small'>
              <Trans i18nKey='emailCapture.agreement' ns='@mc/certificates'>
                <a href={paths.terms} className='mc-text--link' />
                <a href={paths.privacy} className='mc-text--link' />
              </Trans>
            </p>
          </div>
        </form>
      )}
    />
  )
}
